import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { TextSelection } from '@tiptap/pm/state'
import { buildResourceTableInsertContent } from '../lib/post_block_indent.js'
import {
  findSubtitleChildIndex,
  lastTitleChildIndex,
  paragraphTextEndPos,
  resourceRowHasSubtitle,
  rowChildStartPos,
} from '../lib/resource_row_paragraph.js'

export const ResourceTableExtension = Node.create({
  name: 'resourceTable',

  group: 'block',

  content: '(downloadRow | externalLinkRow | meetingRow | audioRow)+',

  defining: true,

  addAttributes() {
    return {}
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-resource]',
        contentElement: 'tbody',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    // Create the table structure
    const table = [
      'table',
      { class: 'post-resource-table__table' },
      [
        'colgroup',
        {},
        ['col', { class: 'post-resource-table__col post-resource-table__col--icon' }],
        ['col', { class: 'post-resource-table__col post-resource-table__col--title' }]
      ],
      [
        'tbody',
        {},
        0 // This will be replaced with the actual rows
      ]
    ];

    const elementAttrs = mergeAttributes(HTMLAttributes, {
      'data-resource': '',
      class: 'post-resource-table'
    });

    return ['div', elementAttrs, table];
  },

  addCommands() {
    return {
      insertResourceTable:
        () =>
        ({ commands, state }) => {
          return commands.insertContent(buildResourceTableInsertContent(state, []))
        },
    }
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('resourceTableBackspaceHandler'),
        appendTransaction: (transactions, oldState, newState) => {
          // Clean up any downloadRow that was accidentally created when backspace merges empty paragraph into resourceTable
          let tr = null;
          const { doc, selection } = newState;
          
          // Check if we just deleted something (transaction has deletions)
          let hadDeletion = false;
          transactions.forEach(transaction => {
            transaction.steps.forEach(step => {
              if (step.from !== step.to) {
                hadDeletion = true;
              }
            });
          });
          
          try {
            // Walk through the document to find resourceTables that have empty downloadRows
            doc.descendants((node, pos) => {
              if (node.type.name === 'resourceTable' && node.childCount > 0) {
                // Iterate backwards to avoid index issues when deleting.
                // NOTE: use `childCount`, not `content.size` (positional size).
                const childCount = node.childCount;
                for (let index = childCount - 1; index >= 0; index--) {
                  let row;
                  try {
                    row = node.content.child(index);
                  } catch (e) {
                    // Index out of range, skip
                    break;
                  }

                  if (!row) continue;

                  if (row.type.name === 'downloadRow' || row.type.name === 'externalLinkRow' || row.type.name === 'meetingRow') {
                    const paragraph = row.content.firstChild;
                    // Check if this row is empty or has only default text
                    const isEmpty = paragraph && (paragraph.content.size === 0 ||
                        (paragraph.content.size === 1 && paragraph.content.firstChild?.type.name === 'hardBreak'));
                    const hasDefaultText = paragraph && paragraph.textContent &&
                        (paragraph.textContent === 'Click to edit title' || paragraph.textContent.trim() === '');

                    // Check if this row has no data (was accidentally created)
                    const hasNoData = !row.attrs.resourceId && !row.attrs.href && !row.attrs.filename;

                    // Remove if: has no data AND (is empty OR has default text) AND there's more than one row
                    if (hasNoData && (isEmpty || hasDefaultText) && childCount > 1) {
                      // This is an unwanted row that was accidentally created - remove it
                      if (!tr) tr = newState.tr;
                      const rowStartPos = pos + 1; // Position after table opening
                      // Calculate actual row position by summing previous rows
                      let rowPos = rowStartPos;
                      for (let i = 0; i < index && i < childCount; i++) {
                        try {
                          const prevRow = node.content.child(i);
                          if (prevRow) rowPos += prevRow.nodeSize;
                        } catch (e) {
                          break;
                        }
                      }
                      
                      // Delete the row
                      try {
                        tr.delete(rowPos, rowPos + row.nodeSize);
                        
                        // After deletion, move cursor to the first row's title area
                        const tableNode = tr.doc.nodeAt(pos);
                        if (tableNode && tableNode.content.size > 0) {
                          const firstRow = tableNode.content.firstChild;
                          if (firstRow) {
                            const firstRowStartPos = pos + 1;
                            const titlePos = firstRowStartPos + 1;
                            try {
                              const titleResolved = tr.doc.resolve(titlePos);
                              if (titleResolved) {
                                tr.setSelection(TextSelection.near(titleResolved));
                              }
                            } catch (e) {
                              // Ignore selection errors
                            }
                          }
                        }
                        tr.scrollIntoView();
                        return false; // Stop iteration after first deletion
                      } catch (e) {
                        // Error during deletion, skip this row
                        console.warn('Error deleting empty row:', e);
                      }
                    }
                  }
                }
              }
            });
          } catch (e) {
            // Error during descendants iteration, return null to avoid further issues
            console.warn('Error in appendTransaction:', e);
            return null;
          }
          
          return tr;
        },
        props: {
          handleKeyDown: (view, event) => {
            // Handle backspace in empty paragraph after resourceTable
            // This handler should run BEFORE image/video handlers
            if (event.key !== 'Backspace') return false;
            
            const { state } = view;
            const { selection } = state;
            const { $from } = selection;
            
            // Check if we're in an empty paragraph
            const currentNode = $from.node($from.depth);
            
            if (currentNode.type.name !== 'paragraph') {
              return false;
            }
            
            const isEmpty = currentNode.content.size === 0 || 
                           (currentNode.content.size <= 2 && currentNode.textContent.trim() === '');
            
            if (!isEmpty) {
              return false;
            }
            
            // Check if we're at the start of the paragraph (for empty paragraphs, this should be true)
            const isAtStart = $from.parentOffset === 0 || 
                             ($from.pos === $from.start($from.depth) && currentNode.content.size === 0);
            
            if (!isAtStart) {
              return false;
            }
            
            const paragraphPos = $from.before($from.depth);
            
            if (paragraphPos <= 0) {
              return false;
            }
            
            // Check if we're inside a list
            let isInList = false;
            for (let depth = $from.depth; depth >= 0; depth--) {
              const nodeAtDepth = $from.node(depth);
              if (nodeAtDepth.type.name === 'listItem') {
                isInList = true;
                break;
              }
            }
            
            let foundResourceTable = null;
            let foundResourceTablePos = null;
            
            // Helper to check if there's a resourceTable before the paragraph
            const checkForResourceTable = () => {
              const resolved = state.doc.resolve(paragraphPos);

              // Check previous sibling first (most reliable)
              if (resolved.depth > 0) {
                const parent = resolved.parent;
                // IMPORTANT: `resolved.parentOffset` is a *positional* offset
                // within the parent (sum of preceding child nodeSizes), not a
                // child index. Using it with `parent.child(...)` would call
                // `Fragment.child(<positional offset>)` and throw a
                // RangeError as soon as the parent has more than two
                // small children (e.g. a blockquote with 4 paragraphs ->
                // parentOffset can be 19+ while childCount is just 4).
                const paragraphIndex = resolved.index(resolved.depth);

                if (paragraphIndex > 0 && paragraphIndex <= parent.childCount) {
                  const prevSibling = parent.child(paragraphIndex - 1);
                  if (prevSibling && prevSibling.type.name === 'resourceTable') {
                    let siblingPos = resolved.start(resolved.depth);
                    for (let j = 0; j < paragraphIndex - 1; j++) {
                      siblingPos += parent.child(j).nodeSize;
                    }
                    return { node: prevSibling, pos: siblingPos };
                  }

                  // If previous sibling is image/video, continue checking
                  if (prevSibling && (prevSibling.type.name === 'image' || prevSibling.type.name === 'video')) {
                    // Check further back
                    for (let i = paragraphIndex - 2; i >= 0; i--) {
                      const earlierSibling = parent.child(i);
                      if (earlierSibling && earlierSibling.type.name === 'resourceTable') {
                        let siblingPos = resolved.start(resolved.depth);
                        for (let j = 0; j < i; j++) {
                          siblingPos += parent.child(j).nodeSize;
                        }
                        return { node: earlierSibling, pos: siblingPos };
                      }
                      if (earlierSibling && earlierSibling.isBlock && earlierSibling.type.name !== 'resourceTable') {
                        break;
                      }
                    }
                  }
                }
              }
              
              // Fallback: walk backwards through document
              let searchPos = paragraphPos - 1;
              while (searchPos >= 0) {
                const nodeAtPos = state.doc.nodeAt(searchPos);
                if (!nodeAtPos) break;
                
                if (nodeAtPos.type.name === 'resourceTable') {
                  return { node: nodeAtPos, pos: searchPos };
                }
                
                if (nodeAtPos.type.name === 'image' || nodeAtPos.type.name === 'video') {
                  searchPos = searchPos - nodeAtPos.nodeSize;
                  continue;
                }
                
                if (nodeAtPos.isBlock) break;
                searchPos = searchPos - nodeAtPos.nodeSize;
              }
              
              return null;
            };
            
            if (isInList) {
              // Inside list: use parent structure to check siblings
              const resolved = state.doc.resolve(paragraphPos);
              if (resolved.depth > 0) {
                // Find the immediate parent that contains block-level children
                let parentNode = null;
                let parentStart = null;
                let paragraphOffset = -1;
                
                // Walk up to find a parent that has block children (listItem)
                for (let depth = resolved.depth; depth >= 0; depth--) {
                  const nodeAtDepth = resolved.node(depth);
                  if (nodeAtDepth.type.name === 'listItem') {
                    parentNode = nodeAtDepth;
                    parentStart = resolved.start(depth);
                    // Find paragraph offset within this parent.
                    // NOTE: `parentNode.content.size` is the *positional* size
                    // of the fragment (sum of child node sizes), not the child
                    // count. Use `childCount` to bound the loop or `child()`
                    // will throw a RangeError on indices > childCount - 1.
                    let currentPos = parentStart + 1;
                    for (let i = 0; i < parentNode.childCount; i++) {
                      const child = parentNode.content.child(i);
                      if (Math.abs(currentPos - paragraphPos) < 5) {
                        paragraphOffset = i;
                        break;
                      }
                      currentPos += child.nodeSize;
                    }
                    break;
                  }
                }
                
                if (parentNode && parentStart !== null && paragraphOffset >= 0) {
                  // Check previous siblings in the same parent
                  for (let i = paragraphOffset - 1; i >= 0; i--) {
                    const sibling = parentNode.content.child(i);
                    
                    if (sibling.type.name === 'resourceTable') {
                      // Calculate position
                      let siblingPos = parentStart + 1;
                      for (let j = 0; j < i; j++) {
                        siblingPos += parentNode.content.child(j).nodeSize;
                      }
                      foundResourceTable = sibling;
                      foundResourceTablePos = siblingPos;
                      break;
                    }
                    
                    // If it's an image or video, continue searching
                    if (sibling.type.name === 'image' || sibling.type.name === 'video') {
                      continue;
                    }
                    
                    // If it's another block, stop
                    if (sibling.isBlock) break;
                  }
                }
              }
            } else {
              // Not in list: try multiple methods to find resourceTable
              // Method 1: Check using resolved parent structure (most reliable)
              const resolved = state.doc.resolve(paragraphPos);
              
              if (resolved.depth >= 0) {
                // Handle both depth 0 (document) and depth > 0 (nested)
                const parent = resolved.depth === 0 ? state.doc : resolved.parent;

                // Resolved.index(depth) returns the *child index* of the node
                // at that depth (works for both doc-level and nested cases).
                // Previously this used `resolved.parentOffset`, which is a
                // positional offset (sum of preceding sibling nodeSizes), so
                // for a paragraph deep inside a multi-child parent (e.g. a
                // blockquote with several paragraphs) this would explode and
                // call `parent.content.child(<positional offset>)` -> throws.
                const paragraphIndex = resolved.index(resolved.depth);

                // Check previous siblings (bounded by childCount, not the
                // positional `content.size`, which would also throw).
                if (paragraphIndex > 0 && paragraphIndex <= parent.childCount) {
                  for (let i = paragraphIndex - 1; i >= 0; i--) {
                    const sibling = parent.content.child(i);

                    if (sibling.type.name === 'resourceTable') {
                      // Calculate position
                      let siblingPos = resolved.depth === 0 ? 1 : resolved.start(resolved.depth);
                      for (let j = 0; j < i; j++) {
                        siblingPos += parent.content.child(j).nodeSize;
                      }
                      foundResourceTable = sibling;
                      foundResourceTablePos = siblingPos;
                      break;
                    }
                    // If it's an image or video, continue
                    if (sibling.type.name === 'image' || sibling.type.name === 'video') {
                      continue;
                    }
                    // If it's another block, stop
                    if (sibling.isBlock) {
                      break;
                    }
                  }
                }
              }
              
              // Method 2: Fallback - walk backwards
              if (!foundResourceTable) {
                const result = checkForResourceTable();
                if (result) {
                  foundResourceTable = result.node;
                  foundResourceTablePos = result.pos;
                }
              }
            }
            
            if (foundResourceTable && foundResourceTablePos !== null) {
              // Prevent default backspace behavior FIRST to avoid creating download rows
              event.preventDefault();
              event.stopPropagation();
              
              // Delete the empty paragraph and move cursor to row title
              const tr = state.tr;
              
              // Delete the paragraph
              tr.delete(paragraphPos, paragraphPos + currentNode.nodeSize);
              
              // After deletion, the table position is still valid (we deleted after it)
              const updatedTableNode = tr.doc.nodeAt(foundResourceTablePos);
              
              if (updatedTableNode && updatedTableNode.childCount > 0) {
                const lastRow = updatedTableNode.child(updatedTableNode.childCount - 1);
                const rowStartPos = foundResourceTablePos + 1;
                let offset = 0;
                for (let i = 0; i < updatedTableNode.childCount - 1; i++) {
                  offset += updatedTableNode.child(i).nodeSize;
                }
                const lastRowPos = rowStartPos + offset;
                const targetIndex = resourceRowHasSubtitle(lastRow)
                  ? findSubtitleChildIndex(lastRow)
                  : lastTitleChildIndex(lastRow);
                const targetParagraph = lastRow.child(targetIndex);
                if (targetParagraph?.type.name === 'paragraph') {
                  const targetParaPos = rowChildStartPos(lastRowPos, lastRow, targetIndex);
                  const cursorPos = paragraphTextEndPos(targetParaPos, targetParagraph);
                  tr.setSelection(TextSelection.create(tr.doc, cursorPos));
                }
              }
              
              tr.scrollIntoView();
              view.dispatch(tr);
              
              return true;
            }
            
            return false;
          },
        },
      }),
    ];
  },
})
